'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { NO_COUNTERPARTS, type Counterparts } from '@/domain/counterparts';
import type { Directory } from '@/domain/directory';

/**
 * The directory for CLIENT components: names the viewer may see, loaded once
 * per request by the root layout (`getDirectory`) and handed down here so a
 * client-side `resolveActor(ref, useDirectory(), directory)` renders real names.
 *
 * It lives as long as the layout does on the client, so across soft
 * navigations it is a name cache: a person who first appears after the last
 * full render resolves on the next full load. Acceptable for names.
 *
 * Beside it rides the person pickers' shortlist source (`getCounterparts`,
 * docs/DESIGN_SYSTEM.md "The person shortlist"), read with `useCounterparts`.
 * It refreshes with the layout, as the names do.
 */
const DirectoryContext = createContext<Directory>({});
const CounterpartsContext = createContext<Counterparts>(NO_COUNTERPARTS);

export function DirectoryProvider({
  value,
  counterparts = NO_COUNTERPARTS,
  children,
}: {
  value: Directory;
  counterparts?: Counterparts;
  children: ReactNode;
}) {
  return (
    <DirectoryContext.Provider value={value}>
      <CounterpartsContext.Provider value={counterparts}>{children}</CounterpartsContext.Provider>
    </DirectoryContext.Provider>
  );
}

export function useDirectory(): Directory {
  return useContext(DirectoryContext);
}

/** The viewer's own ref and the refs ranked for them: what a person picker
 *  shows while its search is empty (`shortlistOf`, src/domain/counterparts.ts). */
export function useCounterparts(): Counterparts {
  return useContext(CounterpartsContext);
}
